import React, { useState } from 'react';
import Layout from '../components/Layout';
import PLReportCard from '../components/PLReportCard';
import { reconciliation } from '../services/api';
import { UploadCloud, AlertTriangle, RefreshCw } from 'lucide-react';

const reshape = (d: any) => ({
  ...d,
  cogs: { total: d.cogs_total || 0, breakdown: d.cogs_breakdown || {}, percentage: d.cogs_percentage || 0 },
  prints: { total: d.prints_total || 0, breakdown: d.prints_breakdown || {}, percentage: d.prints_percentage || 0 },
  shipping: { total: d.shipping_total || 0, breakdown: d.shipping_breakdown || {}, percentage: d.shipping_percentage || 0 },
  gateway: { total: d.gateway_total || 0, breakdown: d.gateway_breakdown || {}, percentage: d.gateway_percentage || 0 },
  marketing: { total: d.marketing_total || 0, breakdown: d.marketing_breakdown || {}, percentage: d.marketing_percentage || 0 },
  fixed: { total: d.fixed_total || 0, breakdown: d.fixed_breakdown || {}, percentage: d.fixed_percentage || 0 },
  total_expenses: d.total_expenses || 0,
});

const PLExcelCheck: React.FC = () => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [raw, setRaw] = useState<any | null>(() => {
    try {
      const saved = localStorage.getItem('pl_excel_check_result');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const handleFile = async (file: File) => {
    setLoading(true);
    setError('');
    try {
      const res = await reconciliation.plExcel(file);
      setRaw(res.data);
      localStorage.setItem('pl_excel_check_result', JSON.stringify(res.data));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to parse file');
    }
    setLoading(false);
  };

  const reset = () => { setRaw(null); setError(''); localStorage.removeItem('pl_excel_check_result'); };

  const data = raw ? reshape(raw) : null;
  const unmapped = data?.cogs.breakdown['Blank Materials (Excel)']?.unmapped_designs || [];

  return (
    <Layout title="P&L (Excel Check)" subtitle={
      data ? `${raw.excel_date_range.from} → ${raw.excel_date_range.to} — Blank Materials from uploaded Excel` : undefined
    }>
      {!data && (
        <div className="animate-in" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 24, maxWidth: 520,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
            Upload Shopify Export
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
            Same file you use for Blank Material Check — a Shopify orders export for a month (non-paid orders are excluded automatically).
            Revenue and other costs come from the app as usual; only the Blank Materials line is
            recalculated from units sold per design in this file × blank cost price.
          </div>
          <label
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={e => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files[0];
              if (f) handleFile(f);
            }}
            style={{
              display: 'block', border: `2px dashed ${dragActive ? '#2dd4a0' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)', padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
          >
            <input
              type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <UploadCloud size={28} color="var(--text-muted)" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {loading ? 'Calculating…' : 'Drop the .xlsx file here, or click to browse'}
            </div>
          </label>
          {error && (
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--accent-red)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '680px 1fr', gap: 20, alignItems: 'start' }}>
          <div>
            <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {raw.paid_orders} paid orders{raw.skipped_rows > 0 && ` · ${raw.skipped_rows} row(s) skipped (unparseable date)`}
                {raw.excluded_non_paid_orders > 0 && ` · ${raw.excluded_non_paid_orders} non-paid order(s) excluded (${raw.excluded_non_paid_units} units)`}
              </div>
              <button
                onClick={reset}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-card)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 14px',
                  fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer',
                }}
              >
                <RefreshCw size={13} /> Upload another file
              </button>
            </div>

            <PLReportCard data={data} className="animate-in-1" />
          </div>

          {unmapped.length > 0 && (
            <div className="animate-in-2" style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', overflow: 'hidden',
            }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-red)' }}>Excluded from Blank Materials</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                  No mapping or no cost price — map these on Design Mappings to include them.
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Design', 'Units'].map((h, i) => (
                      <th key={h} style={{ padding: '8px 20px', textAlign: i === 0 ? 'left' : 'right', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {unmapped.map((u: any) => (
                    <tr key={u.design} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '9px 20px', fontSize: 12.5, color: 'var(--text-primary)' }}>{u.design}</td>
                      <td style={{ padding: '9px 20px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>{u.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default PLExcelCheck;
